import React, { useState } from 'react';
import { Popover, Menu, MenuItem, Position, Icon } from '@blueprintjs/core';
import { observer } from 'mobx-react-lite';
import { NodeStatus, getStatusConfig, ALL_STATUSES } from '../../constants/nodeStatus';
import './StatusSelector.css';

interface StatusSelectorProps {
  currentStatus?: NodeStatus | null;
  onStatusSelect: (status: NodeStatus | null) => void;
  minimal?: boolean;
  showLabel?: boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = observer(({
  currentStatus,
  onStatusSelect,
  minimal = false,
  showLabel = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentConfig = getStatusConfig(currentStatus);

  const renderStatusBadge = (status: NodeStatus | null, isPlaceholder = false) => {
    if (isPlaceholder) {
      return (
        <div className="status-badge status-badge-placeholder">
          <Icon icon="plus" size={10} />
        </div>
      );
    }

    const config = getStatusConfig(status);
    if (!config) {
      return null;
    }

    return (
      <div 
        className="status-badge" 
        style={{
          backgroundColor: config.backgroundColor,
          border: `1px solid ${config.borderColor}`,
          color: config.color,
        }}
      >
        <Icon icon={config.icon as any} size={10} />
        <span className="status-label">{config.label}</span>
      </div>
    );
  };

  const statusMenu = (
    <Menu>
      <MenuItem
        text="No Status"
        icon="cross"
        onClick={() => {
          onStatusSelect(null);
          setIsOpen(false);
        }}
      />
      {ALL_STATUSES.map((statusConfig) => (
        <MenuItem
          key={statusConfig.value}
          text={statusConfig.label}
          icon={statusConfig.icon as any}
          onClick={() => {
            onStatusSelect(statusConfig.value);
            setIsOpen(false);
          }}
          active={currentStatus === statusConfig.value}
          labelElement={
            <div 
              className="status-color-indicator"
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: statusConfig.color,
              }}
            />
          }
        />
      ))}
    </Menu>
  );

  if (minimal) {
    // Minimal mode for showing on nodes
    return (
      <Popover
        content={statusMenu}
        position={Position.BOTTOM_LEFT}
        isOpen={isOpen}
        onInteraction={(nextOpenState) => {
          setIsOpen(nextOpenState);
        }}
        minimal={true}
        usePortal={true}
        portalContainer={document.body}
        popoverClassName="status-selector-popover"
        modifiers={{
          preventOverflow: { 
            enabled: true,
            boundariesElement: 'viewport'
          },
          flip: { 
            enabled: true,
            behavior: ['bottom', 'top', 'left', 'right']
          }
        }}
        canEscapeKeyClose={true}
        interactionKind="click"
      >
        <div className="status-selector-minimal" style={{ cursor: 'pointer' }}>
          {currentConfig ? renderStatusBadge(currentStatus) : renderStatusBadge(null, true)}
        </div>
      </Popover>
    );
  }

  // Full mode for node details panel
  return (
    <div className="status-selector">
      {showLabel && <label className="status-selector-label">Status</label>}
      <Popover
        content={statusMenu}
        position={Position.BOTTOM_LEFT}
        isOpen={isOpen}
        onInteraction={(state) => setIsOpen(state)}
        usePortal={true}
        portalContainer={document.body}
        popoverClassName="status-selector-popover"
        modifiers={{
          preventOverflow: { 
            enabled: true,
            boundariesElement: 'viewport'
          },
          flip: { 
            enabled: true,
            behavior: ['bottom', 'top', 'left', 'right']
          }
        }}
        canEscapeKeyClose={true}
      >
        <div className="status-selector-button" style={{ cursor: 'pointer' }}>
          {currentConfig ? renderStatusBadge(currentStatus) : (
            <div className="status-selector-placeholder">
              <Icon icon="plus" size={12} />
              <span>Set Status</span>
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
});

StatusSelector.displayName = 'StatusSelector';

export default StatusSelector;


